<script setup>
import {USE_CATEGORY_TAG_KEY} from "./CategoryTagComposition";
import {computed, inject, ref} from "vue";
import CampaignTagButton from "./CampaignTagButton.vue";

const props = defineProps({
  _siteUrl: {
    type: String,
    required: true
  }
});

//コンポジションの注入
const composition = inject(USE_CATEGORY_TAG_KEY);

//チェックされているタグ ID 配列
const campaignChecks = ref([]);

//今キャンペーン登録されているタグを抜き出す
const campaignedTags = computed(() => {
  //最終的な配列
  const results = [];

  //カテゴリごとにタグを検索
  for (let i = 0, max = composition.state.categories.length; i  < max; i = (i + 1) | 0) {
    for (const tag of composition.state.categories[i].tags) {
      //既に追加済ならスルー
      if (results.some(result => Number(result.tag_id) === Number(tag.id))) {
        continue;
      }

      //タグ ID に紐づくキャンペーンを検索
      const campaign = composition.state.campaigns.find(campaign => Number(campaign.tag_id) == Number(tag.id));

      //キャンペーンが存在しなければスルー
      if ( ! campaign) {
        continue;
      }

      //結果に追加
      results.push({
        tag_id: Number(tag.id),
        tag_name: tag.name,
        category_name: composition.state.categories[i].name,
        is_ratio: Number(campaign.ratio) > 0
      });
    }
  }

  //返す
  return results;
});

//エラー
const errors = ref([]);

//成功画面を表示する
const viewToast = () => {
  //成功表示用のトーストを得る
  const toast = document.querySelector('.js-successToast');

  //トーストがあれば表示
  if (toast) {
    toast.style.display = '';
    toast.classList.add('isDsply');
    setTimeout(() => { toast.classList.remove('isDsply') }, 1500);
  }
};

/**
 * 指定されたタグのチェック状態を変更する
 * @param tagId
 * @param check
 */
const changeCheck = payload => {
  //現状の配列に指定タグ ID があるか判定
  const index = campaignChecks.value.findIndex(currentTagId => Number(currentTagId) === Number(payload.tagId));

  //現状存在しない & check が true なら追加
  if (index === -1 && payload.check) {
    campaignChecks.value.push(Number(payload.tagId));
  }

  //現状存在刷る check が false なら削除
  else if (index !== -1 && ! payload.check) {
    campaignChecks.value.splice(index, 1);
  }
};

/**
 * チェックされているキャンペーンを解除する
 * @returns {Promise<void>}
 */
const releaseCampaign = async () => {
  //チェックされているキャンペーンが無ければなにもしない
  if (campaignChecks.value.length < 1) {
    return;
  }

  //サーバーに変更を依頼
  const result = await composition.releaseCampaigns(campaignChecks.value);

  //成功表示
  if (result) {
    //トーストを出す
    viewToast();

    //成功したらチェックを全て外す
    campaignChecks.value = [];
  }

  //失敗したらエラーを表示
  else {
    errors.value = composition.state.errors;
  }
};

/**
 * チェックされているキャンペーンを解除する
 * @returns {Promise<void>}
 */
const enableCampaign = async () => {
  //チェックされているキャンペーンが無ければなにもしない
  if (campaignChecks.value.length < 1) {
    return;
  }

  //サーバーに変更を依頼
  const result = await composition.enableCampaigns(campaignChecks.value);

  //成功表示
  if (result) {
    //トーストを出す
    viewToast();

    //成功したらチェックを全て外す
    campaignChecks.value = [];
  }

  //失敗したらエラーを表示
  else {
    errors.value = composition.state.errors;
  }
};

/**
 * チェックされているキャンペーンを削除する
 * @returns {Promise<void>}
 */
const deleteCampaign = async () => {
  //チェックされているキャンペーンが無ければなにもしない
  if (campaignChecks.value.length < 1) {
    return;
  }

  //サーバーに変更を依頼
  const result = await composition.deleteCampaigns(campaignChecks.value);

  //成功表示
  if (result) {
    //トーストを出す
    viewToast();

    //成功したらチェックを全て外す
    campaignChecks.value = [];
  }

  //失敗したらエラーを表示
  else {
    errors.value = composition.state.errors;
  }
};
</script>

<template>
  <section class="modBasicSec">
    <h2 class="modBasicSecTitle">キャンペーンタグ設定（ポイント倍率アップ）</h2>
    <div class="modBasicSecContents">
      <div class="pageCmpgnSetArea">
        <ul class="pageCmpgnSetList">
          <li><button
              type="button"
              class="pageCmpgnSetBtn mdSet"
              @click="enableCampaign"
          >設定</button></li>
          <li><button
              type="button"
              class="pageCmpgnSetBtn mdCancel"
              @click="releaseCampaign"
          >解除</button></li>
          <li><button
              type="button"
              class="pageCmpgnSetBtn mdDelete"
              @click="deleteCampaign"
          >削除</button></li>
        </ul>
        <p
            v-if="campaignedTags.length < 1"
            class="pageCmpgnSetNote"
        >※キャンペーンタグに設定したいタグをカテゴリーから追加してください</p>
        <p
            v-else
            class="pageCmpgnSetNote"
        >※ポイント倍率アップするタグをチェックして、設定ボタンをクリックしてください<br>※ここで削除してもカテゴリーにタグは残ります</p>
      </div>
      <div class="pageCmpgnTagArea">
        <ul class="pageCmpgnTagList">
          <CampaignTagButton
              v-for="tag in campaignedTags"
              :key="tag.tag_id"
              :tag-name="tag.tag_name"
              :category-name="tag.category_name"
              :tag-id="tag.tag_id"
              :is-setting="tag.is_ratio"
              :checked="campaignChecks.some(campaign => Number(campaign) === Number(tag.tag_id))"
              :_site-url="_siteUrl"
              @change-check="changeCheck"
          />
        </ul>
      </div>
    </div>
  </section>
</template>
